const CALLBACK_DEFINITIONS = Object.freeze({
    'verify-email': {
        apiPath: '/email/verify/{id}/{hash}',
        requiresAuth: false,
        feedbackQueryKey: 'email',
        feedback: {
            success: { queryValue: 'verified', severity: 'success', translationKey: 'login.verified' },
            failed: { queryValue: 'unverified', severity: 'warn', translationKey: 'login.email-validation-link-expired' },
        },
    },
    'join-team': {
        apiPath: '/teams/{team}/sharing/join',
        memberPath: '/teams/{team}',
        requiresAuth: true,
        feedbackQueryKey: 'team-sharing',
        feedback: {
            success: { queryValue: 'success', severity: 'success', translationKey: 'team.team-joined' },
            failed: { queryValue: 'failed', severity: 'warn', translationKey: 'error.there-was-error' },
        },
    },
    'join-session': {
        apiPath: '/sessions/{session}/sharing/join',
        memberPath: '/sessions/{session}',
        requiresAuth: true,
        feedbackQueryKey: 'session-sharing',
        feedback: {
            success: { queryValue: 'success', severity: 'success', translationKey: 'session.session-joined' },
            failed: { queryValue: 'failed', severity: 'warn', translationKey: 'error.there-was-error' },
        },
    },
    'join-horse': {
        apiPath: '/horses/{horse}/sharing/join',
        memberPath: '/horses/{horse}',
        requiresAuth: true,
        feedbackQueryKey: 'horse-sharing',
        feedback: {
            success: { queryValue: 'success', severity: 'success', translationKey: 'horse.horse-joined' },
            failed: { queryValue: 'failed', severity: 'warn', translationKey: 'error.there-was-error' },
        },
    },
})

const CALLBACK_QUERY_KEYS = ['callback', 'id', 'hash', 'team', 'session', 'horse', 'signature', 'token', 'expires']
const TRANSIENT_QUERY_KEYS = ['email', 'team-sharing', 'session-sharing', 'horse-sharing', 'toast-preview', 'toastPreview']

let pendingRedirectTarget

export const normalizeQueryValue = (value) => (Array.isArray(value) ? value[0] : value)

const getCallbackDefinition = (query) => {
    const callback = normalizeQueryValue(typeof query === 'string' ? query : query?.callback)
    return callback ? CALLBACK_DEFINITIONS[callback] : undefined
}

export const getCallbackName = (query) => {
    const callback = normalizeQueryValue(typeof query === 'string' ? query : query?.callback)
    return callback && getCallbackDefinition(callback) ? callback : undefined
}

export const callbackRequiresAuth = (callback) => Boolean(getCallbackDefinition(callback)?.requiresAuth)

export const CALLBACK_QUERY_FEEDBACK = Object.freeze(
    Object.values(CALLBACK_DEFINITIONS).reduce((feedbackByQueryKey, definition) => {
        if (!definition.feedbackQueryKey || !definition.feedback) return feedbackByQueryKey

        feedbackByQueryKey[definition.feedbackQueryKey] = Object.fromEntries(
            Object.values(definition.feedback).map(({ queryValue, severity, translationKey }) => [
                queryValue,
                { severity, translationKey },
            ])
        )

        return feedbackByQueryKey
    }, {})
)

const sanitizeRedirectTarget = (target) => {
    if (!target || typeof target !== 'string') return undefined

    try {
        const parsedTarget = new URL(target, 'https://app.alogo.io')
        const query = Object.fromEntries(parsedTarget.searchParams.entries())
        const callback = getCallbackName(query)

        if (callback && !callbackRequiresAuth(callback)) {
            CALLBACK_QUERY_KEYS.forEach((key) => parsedTarget.searchParams.delete(key))
            TRANSIENT_QUERY_KEYS.forEach((key) => parsedTarget.searchParams.delete(key))
        }

        const search = parsedTarget.searchParams.toString()
        return `${parsedTarget.pathname || '/'}${search ? `?${search}` : ''}${parsedTarget.hash || ''}`
    } catch {
        return undefined
    }
}

export const buildLoginRedirectTarget = (currentRoute) => {
    const target = currentRoute?.fullPath || currentRoute?.path || '/'
    return sanitizeRedirectTarget(target) || '/'
}

export const buildLoginRedirectLocation = (currentRoute) => ({
    name: 'auth.login',
    query: {
        redirect: getLoginRedirectTarget(currentRoute),
    },
})

export const readRedirectTarget = (value) => {
    const target = normalizeQueryValue(value)
    if (!target || typeof target !== 'string') return undefined
    // Only allow relative paths — reject protocol-relative URLs (//example.com)
    if (!target.startsWith('/') || target.startsWith('//')) return undefined
    return sanitizeRedirectTarget(target)
}

export const getPendingRedirectTarget = () => pendingRedirectTarget

export const rememberPendingRedirectTarget = (target) => {
    const nextTarget =
        typeof target === 'string' ? readRedirectTarget(target) : buildLoginRedirectTarget(target)

    pendingRedirectTarget = nextTarget
    return pendingRedirectTarget
}

export const clearPendingRedirectTarget = () => {
    pendingRedirectTarget = undefined
}

const getLoginRedirectTarget = (currentRoute) => {
    if (currentRoute?.name === 'auth.login') {
        return readRedirectTarget(currentRoute?.query?.redirect) || getPendingRedirectTarget() || '/'
    }

    if (currentRoute?.fullPath || currentRoute?.path) {
        return buildLoginRedirectTarget(currentRoute)
    }

    return getPendingRedirectTarget() || '/'
}

export const getPostAuthRedirectTarget = (currentRoute) => {
    const redirectTarget = readRedirectTarget(currentRoute?.query?.redirect)
    if (redirectTarget) return redirectTarget

    return getPendingRedirectTarget() || { name: 'index' }
}

const buildNativePath = (url) => {
    if (url.protocol === 'http:' || url.protocol === 'https:') {
        return url.pathname || '/'
    }

    const hostSegment = url.host && !url.host.includes('.') ? `/${url.host}` : ''
    const pathSegment = url.pathname && url.pathname !== '/' ? url.pathname : ''

    return (`${hostSegment}${pathSegment}` || '/').replace(/\/{2,}/g, '/')
}

export const parseIncomingUrl = (rawUrl) => {
    const parsedUrl = new URL(rawUrl)
    return {
        path: buildNativePath(parsedUrl),
        query: Object.fromEntries(parsedUrl.searchParams.entries()),
        hash: parsedUrl.hash || undefined,
    }
}

export const buildCallbackApiUrl = (queryParams) => {
    const callbackDefinition = getCallbackDefinition(queryParams)
    if (!callbackDefinition) return undefined

    const id = normalizeQueryValue(queryParams?.id)
    const hash = normalizeQueryValue(queryParams?.hash)
    const team = normalizeQueryValue(queryParams?.team)
    const session = normalizeQueryValue(queryParams?.session)
    const horse = normalizeQueryValue(queryParams?.horse)
    const signature = normalizeQueryValue(queryParams?.signature)
    const token = normalizeQueryValue(queryParams?.token)
    const expires = normalizeQueryValue(queryParams?.expires)

    const apiUrl = callbackDefinition.apiPath
        .replace('{id}', encodeURIComponent(id || ''))
        .replace('{hash}', encodeURIComponent(hash || ''))
        .replace('{team}', encodeURIComponent(team || ''))
        .replace('{session}', encodeURIComponent(session || ''))
        .replace('{horse}', encodeURIComponent(horse || ''))

    const queryString = new URLSearchParams()
    if (token) queryString.append('token', token)
    if (signature) queryString.append('signature', signature)
    if (expires) queryString.append('expires', expires)
    queryString.append('responseType', 'http')

    return `${apiUrl}?${queryString.toString()}`
}

// The page of the horse, team or session a join link adds the person to.
export const buildJoinedModelApiUrl = (queryParams) => {
    const memberPath = getCallbackDefinition(queryParams)?.memberPath
    if (!memberPath) return undefined

    const team = normalizeQueryValue(queryParams?.team)
    const session = normalizeQueryValue(queryParams?.session)
    const horse = normalizeQueryValue(queryParams?.horse)
    if (!team && !session && !horse) return undefined

    return memberPath
        .replace('{team}', encodeURIComponent(team || ''))
        .replace('{session}', encodeURIComponent(session || ''))
        .replace('{horse}', encodeURIComponent(horse || ''))
}

// An invitation accepted at sign-up is used already when the kept link is
// replayed afterwards: the API answers 409 "Invite already used". Only a join
// link's 409 can mean the person did join; every other failure stays an error.
export const isUsedInvitationReplay = (queryParams, error) =>
    Boolean(getCallbackDefinition(queryParams)?.memberPath) && error?.response?.status === 409

// The 409 alone does not prove the person joined (a forwarded link, or someone
// who left since): they did only if they can open what the link joins.
const canOpenJoinedModel = async (client, queryParams) => {
    const url = buildJoinedModelApiUrl(queryParams)
    if (!url) return false

    try {
        await client.get(url)
        return true
    } catch {
        return false
    }
}

export const buildProcessedCallbackQuery = (queryParams, result) => {
    const callback = getCallbackName(queryParams)
    const callbackDefinition = getCallbackDefinition(callback)
    const nextQuery = { ...queryParams }

    if (!callback || !callbackDefinition) {
        return nextQuery
    }

    const feedbackValue = callbackDefinition.feedback?.[result]
    if (callbackDefinition.feedbackQueryKey && feedbackValue?.queryValue) {
        nextQuery[callbackDefinition.feedbackQueryKey] = feedbackValue.queryValue
    }

    CALLBACK_QUERY_KEYS.forEach((key) => delete nextQuery[key])
    return nextQuery
}

export const processCallbackQuery = async ({ client, queryParams, isLoggedIn }) => {
    const callback = getCallbackName(queryParams)
    if (!callback) {
        return { handled: false }
    }

    if (callbackRequiresAuth(callback) && !isLoggedIn) {
        return { handled: false, deferred: true, callback }
    }

    let result = 'failed'

    try {
        const finalUrl = buildCallbackApiUrl(queryParams)
        if (!finalUrl) {
            return { handled: false }
        }

        await client.get(finalUrl)
        result = 'success'
    } catch (error) {
        // On 401, the interceptor will redirect to login. Preserve the
        // callback params so the deeplink can be retried after re-auth.
        if (error?.response?.status === 401) {
            return { handled: false, deferred: true, callback }
        }

        if (isUsedInvitationReplay(queryParams, error) && (await canOpenJoinedModel(client, queryParams))) {
            result = 'success'
        } else {
            console.error('Error processing callback query:', error)
        }
    }

    return {
        handled: true,
        callback,
        result,
        query: buildProcessedCallbackQuery(queryParams, result),
    }
}
